import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useToast } from "@/contexts/ToastContext";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import BranchSubNav from "@/components/branches/BranchSubNav";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { ReAuthModal } from "@/components/auth/ReAuthModal";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { UserPlus, ExternalLink, Clock, Search, Building2, UserMinus, Check } from "lucide-react";
import { branchesApi, type Branch } from "@/lib/api/branches";
import { staffApi, type Staff } from "@/lib/api/staff";
import { cn } from "@/lib/utils";

const statusConfig: Record<string, { label: string; cls: string }> = {
  ACTIVE: { label: "Active", cls: "bg-green-100 text-green-700" },
  PENDING: { label: "Pending", cls: "bg-yellow-100 text-yellow-700" },
  INACTIVE: { label: "Inactive", cls: "bg-gray-100 text-gray-600" },
  SUSPENDED: { label: "Suspended", cls: "bg-red-100 text-red-700" },
};

const BranchStaffPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const [branch, setBranch] = useState<Branch | null>(null);
  const [branchStaff, setBranchStaff] = useState<Staff[]>([]);
  const [allStaff, setAllStaff] = useState<Staff[]>([]);
  const [loading, setLoading] = useState(true);

  // Assign modal state
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignSearch, setAssignSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Remove modal state
  const [removingStaff, setRemovingStaff] = useState<Staff | null>(null);

  // Re-auth state (shared)
  const [reAuthOpen, setReAuthOpen] = useState(false);
  const [reAuthPassword, setReAuthPassword] = useState("");
  const [reAuthAction, setReAuthAction] = useState<"assign" | "remove" | null>(null);
  const [reAuthLoading, setReAuthLoading] = useState(false);

  useEffect(() => {
    if (id) loadData();
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [branchData, staffData] = await Promise.all([
        branchesApi.getById(id!),
        staffApi.getAll(), // fetch ALL staff for the assign modal
      ]);
      setBranch(branchData);
      setAllStaff(staffData);
      setBranchStaff(staffData.filter((s) => s.branchId === id));
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to load branch staff.");
      navigate("/setup/branches");
    } finally {
      setLoading(false);
    }
  };

  // Staff NOT in this branch — shown in assign modal
  const unassignedOrElsewhere = useMemo(
    () => allStaff.filter((s) => s.branchId !== id),
    [allStaff, id]
  );

  const filteredForAssign = useMemo(() => {
    const q = assignSearch.toLowerCase();
    if (!q) return unassignedOrElsewhere;
    return unassignedOrElsewhere.filter(
      (s) =>
        `${s.firstName} ${s.lastName}`.toLowerCase().includes(q) ||
        s.email.toLowerCase().includes(q) ||
        s.role?.name?.toLowerCase().includes(q)
    );
  }, [unassignedOrElsewhere, assignSearch]);

  const toggleSelect = (staffId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(staffId) ? next.delete(staffId) : next.add(staffId);
      return next;
    });
  };

  // --- Assign flow ---
  const handleAssignClick = () => {
    if (selectedIds.size === 0) return;
    setAssignOpen(false);
    setReAuthAction("assign");
    setReAuthPassword("");
    setReAuthOpen(true);
  };

  // --- Remove flow ---
  const handleRemoveClick = (staff: Staff) => {
    setRemovingStaff(staff);
    setReAuthAction("remove");
    setReAuthPassword("");
    setReAuthOpen(true);
  };

  // --- Confirm re-auth ---
  const handleReAuthConfirm = async () => {
    if (!reAuthPassword) return;
    setReAuthLoading(true);
    try {
      if (reAuthAction === "assign") {
        await Promise.all(
          [...selectedIds].map((staffId) =>
            staffApi.update(staffId, { branchId: id }, reAuthPassword)
          )
        );
        toast.success(
          `${selectedIds.size} staff member${selectedIds.size !== 1 ? "s" : ""} assigned to ${branch?.name}.`
        );
        setSelectedIds(new Set());
      } else if (reAuthAction === "remove" && removingStaff) {
        await staffApi.update(removingStaff.id, { branchId: null }, reAuthPassword);
        toast.success(
          `${removingStaff.firstName} ${removingStaff.lastName} removed from ${branch?.name}.`
        );
        setRemovingStaff(null);
      }
      setReAuthOpen(false);
      setReAuthPassword("");
      await loadData();
    } catch (err: any) {
      if (err.response?.status === 401) {
        toast.error("Incorrect password. Please try again.");
      } else {
        toast.error(err.response?.data?.message || "Action failed.");
      }
    } finally {
      setReAuthLoading(false);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <BranchSubNav branch={branch} />

      <PageContainer width="wide">
        <PageHeader
          title="Branch Staff"
          description={`${branchStaff.length} staff member${branchStaff.length !== 1 ? "s" : ""} assigned to ${branch?.name ?? "this branch"}`}
          breadcrumbItems={[
            { label: "Setup", href: "/setup" },
            { label: "Branches", href: "/setup/branches" },
            { label: branch?.name ?? "Branch", href: id ? `/setup/branches/${id}` : undefined },
            { label: "Staff" },
          ]}
          showSearch={false}
          titleSize="md"
          breadcrumbAlign="start"
          actions={
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={() => {
                  setAssignSearch("");
                  setSelectedIds(new Set());
                  setAssignOpen(true);
                }}
                className="gap-2"
                variant="outline"
              >
                <UserPlus className="w-4 h-4" />
                Assign Existing Staff
              </Button>
              <Button
                onClick={() => navigate("/setup/staff/new")}
                className="gap-2"
                variant="outline"
              >
                <UserPlus className="w-4 h-4" />
                Create New Staff
              </Button>
            </div>
          }
        />

        {/* Current staff table */}
        {branchStaff.length === 0 ? (
          <Card className="p-12 text-center">
            <UserPlus className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 font-medium">No staff assigned to this branch</p>
            <p className="text-sm text-gray-400 mt-1">
              Assign existing staff or create a new staff member and set their branch.
            </p>
            <Button
              onClick={() => {
                setAssignSearch("");
                setSelectedIds(new Set());
                setAssignOpen(true);
              }}
              className="mt-4 gap-2"
              variant="outline"
            >
              <UserPlus className="w-4 h-4" />
              Assign Existing Staff
            </Button>
          </Card>
        ) : (
          <Card className="overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">Name</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700 hidden sm:table-cell">
                    Role
                  </th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700 hidden md:table-cell">
                    Department
                  </th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">Status</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700 hidden lg:table-cell">
                    Joined
                  </th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {branchStaff.map((member) => {
                  const statusCfg = statusConfig[member.status] ?? {
                    label: member.status,
                    cls: "bg-gray-100 text-gray-600",
                  };
                  const isManager = branch?.managerId === member.id;
                  return (
                    <tr key={member.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-semibold flex-shrink-0">
                            {member.firstName[0]}
                            {member.lastName[0]}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">
                              {member.firstName} {member.lastName}
                            </p>
                            <p className="text-xs text-gray-500">{member.email}</p>
                          </div>
                          {isManager && (
                            <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">
                              Manager
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-600 hidden sm:table-cell">
                        {member.role?.name ?? <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-4 py-3 text-gray-600 hidden md:table-cell">
                        {member.department?.name ?? <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={cn(
                            "inline-block text-xs font-medium px-2 py-0.5 rounded-full",
                            statusCfg.cls
                          )}
                        >
                          {statusCfg.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-500 hidden lg:table-cell">
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(member.createdAt).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 justify-end">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => navigate(`/setup/staff/${member.id}`)}
                            className="gap-1 text-xs"
                          >
                            <ExternalLink className="w-3 h-3" />
                            View
                          </Button>
                          {!isManager && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleRemoveClick(member)}
                              className="gap-1 text-xs text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              <UserMinus className="w-3 h-3" />
                              Remove
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>
        )}
      </PageContainer>

      {/* ── Assign Staff Modal ── */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-primary" />
              Assign Staff to {branch?.name}
            </DialogTitle>
            <DialogDescription>
              Select one or more staff members to assign. Staff currently assigned to another branch
              will be moved here.
            </DialogDescription>
          </DialogHeader>

          {/* Search */}
          <div className="relative flex-shrink-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search by name, email or role…"
              value={assignSearch}
              onChange={(e) => setAssignSearch(e.target.value)}
              className="pl-9"
              autoFocus
            />
          </div>

          {/* Staff list */}
          <div className="flex-1 overflow-y-auto min-h-0 rounded-md border border-gray-200 divide-y divide-gray-100">
            {filteredForAssign.length === 0 ? (
              <div className="p-8 text-center text-sm text-gray-500">
                {assignSearch
                  ? "No staff match your search."
                  : "All staff are already assigned to this branch."}
              </div>
            ) : (
              filteredForAssign.map((s) => {
                const isSelected = selectedIds.has(s.id);
                return (
                  <button
                    key={s.id}
                    onClick={() => toggleSelect(s.id)}
                    className={cn(
                      "w-full flex items-center gap-3 px-4 py-3 text-left transition-colors",
                      isSelected ? "bg-primary/5" : "hover:bg-gray-50"
                    )}
                  >
                    {/* Checkbox area */}
                    <div
                      className={cn(
                        "w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors",
                        isSelected ? "bg-primary border-primary" : "border-gray-300"
                      )}
                    >
                      {isSelected && <Check className="w-3 h-3 text-white" />}
                    </div>

                    {/* Avatar */}
                    <div className="w-8 h-8 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center text-xs font-semibold flex-shrink-0">
                      {s.firstName[0]}
                      {s.lastName[0]}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">
                        {s.firstName} {s.lastName}
                      </p>
                      <p className="text-xs text-gray-500 truncate">{s.email}</p>
                    </div>

                    {/* Role */}
                    <div className="text-right flex-shrink-0">
                      <p className="text-xs font-medium text-gray-700">{s.role?.name ?? "—"}</p>
                      {s.branch ? (
                        <p className="text-xs text-amber-600 flex items-center gap-1 justify-end">
                          <Building2 className="w-3 h-3" />
                          {s.branch.name}
                        </p>
                      ) : (
                        <p className="text-xs text-gray-400">No branch</p>
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between flex-shrink-0 pt-2 border-t border-gray-100">
            <p className="text-sm text-gray-500">
              {selectedIds.size > 0
                ? `${selectedIds.size} staff member${selectedIds.size !== 1 ? "s" : ""} selected`
                : "Click to select staff"}
            </p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setAssignOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleAssignClick}
                disabled={selectedIds.size === 0}
                className="gap-2"
              >
                <UserPlus className="w-4 h-4" />
                Assign {selectedIds.size > 0 ? `(${selectedIds.size})` : ""}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Re-auth Modal ── */}
      <ReAuthModal
        open={reAuthOpen}
        onOpenChange={(v) => {
          setReAuthOpen(v);
          if (!v) setReAuthPassword("");
        }}
        onConfirm={handleReAuthConfirm}
        password={reAuthPassword}
        onPasswordChange={setReAuthPassword}
        loading={reAuthLoading}
        title={reAuthAction === "assign" ? "Confirm Staff Assignment" : "Confirm Staff Removal"}
        description={
          reAuthAction === "assign"
            ? `You are about to assign ${selectedIds.size} staff member${selectedIds.size !== 1 ? "s" : ""} to ${branch?.name}. Enter your password to confirm.`
            : removingStaff
              ? `You are about to remove ${removingStaff.firstName} ${removingStaff.lastName} from ${branch?.name}. Enter your password to confirm.`
              : "Enter your password to confirm."
        }
      />
    </AppLayout>
  );
};

export default BranchStaffPage;
